import { FC } from 'react';

import { Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { TooltipProps } from 'recharts';

import { LINE_DATA_KEYS } from '@/constants';
import { CURRENCY_SYMBOLS } from '@/contexts/currencyConstants';
import { formatNumberWithCommas } from '@/utils/index';

const isCurrencySymbol = (u: string) =>
  Object.values(CURRENCY_SYMBOLS).includes(u);

import StyledTooltip from './styles';

const getLegendIcon = (
  type: string,
  color: string | undefined,
  dataKey?: string
) => {
  const isLineDataKey = (dataKey: string) => {
    return LINE_DATA_KEYS.includes(dataKey || '');
  };
  if (isLineDataKey(dataKey || '')) {
    return (
      <svg width="11" height="2">
        <line
          x1="0"
          y1="6"
          x2="12"
          y2="6"
          stroke={color}
          strokeWidth="11"
          strokeDasharray="3 1 3 1 3"
          strokeDashoffset="0"
        />
      </svg>
    );
  }
  switch (type) {
    case 'line':
      return (
        <svg width="11" height="2">
          <line
            x1="0"
            y1="6"
            x2="12"
            y2="6"
            stroke={color}
            strokeWidth="11"
            strokeDasharray="3 1 3 1 3"
            strokeDashoffset="0"
          />
        </svg>
      );
    case 'circle':
    default:
      return (
        <svg width="10" height="10">
          <circle cx="5" cy="5" r="5" fill={color} />
        </svg>
      );
  }
};

interface CustomTooltipProps extends TooltipProps<number, string> {
  extraData?: {
    name: string;
    value: string | number | React.ReactNode;
    unit: string;
    color: string;
    type: string;
  }[];
  width?: number;
}

const RGraphicTooltip: FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  extraData,
  width,
}) => {
  const { t } = useTranslation();
  if (active && payload && payload.length) {
    const tooltipLabel = payload[0].payload.name;
    const fullCycleValue = payload[0]?.payload?.fullCycle;
    return (
      <StyledTooltip vertical width={width}>
        <Typography.Text className="fs-14-semi-bold label">
          {label || tooltipLabel}
        </Typography.Text>
        <hr className="line" />
        {fullCycleValue !== undefined && (
          <Flex justify="space-between">
            <Typography.Text className="fs-14-regular text-gray-color">
              {t('result.energy.batteryAndLifeCycle.fullCycle')}
            </Typography.Text>
            <Typography.Text className="fs-14-regular text-gray-color">
              {fullCycleValue}
            </Typography.Text>
          </Flex>
        )}
        {extraData &&
          extraData.map((data, index) => {
            return (
              data.value !== 0 && (
                <Flex key={index} justify="space-between">
                  <Flex gap={4} align="center">
                    {getLegendIcon(data?.type || '', data.color)}
                    <Typography.Text className="fs-14-regular text-gray-color">
                      {data.name}
                    </Typography.Text>
                  </Flex>
                  <Typography.Text className="fs-14-regular">
                    {isCurrencySymbol(data.unit) ? `${data.unit} ` : ''}
                    {data.value}
                    {!isCurrencySymbol(data.unit) ? `${data.unit} ` : ''}
                  </Typography.Text>
                </Flex>
              )
            );
          })}
        {payload
          .slice()
          .reverse()
          .map((item) => {
            const unit = item.unit || item.payload.unit;
            const value = formatNumberWithCommas(Number(item.value));
            return (
              <Flex key={item.dataKey} justify="space-between">
                <Flex gap={4} align="center">
                  {getLegendIcon(
                    item.payload.type || 'circle',
                    item.color || item.payload.fill,
                    item.dataKey?.toString()
                  )}
                  <Typography.Text className="fs-14-regular text-gray-color">
                    {item.name}
                  </Typography.Text>
                </Flex>
                <Typography.Text className="fs-14-regular  text-gray-color">
                  {isCurrencySymbol(unit)
                    ? `${unit} ${value}`
                    : `${value} ${unit || ''}`}
                </Typography.Text>
              </Flex>
            );
          })}
      </StyledTooltip>
    );
  }
  return null;
};

export default RGraphicTooltip;
